import { useEffect, useState } from "react";
import type { AdminIntegrationSettingsDto } from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchAdminIntegrationSettings } from "../../shared/api/adminSettings";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const pageLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/tenant", label: "テナント" }
];

function renderValue(value: string | number | null): string {
  if (value === null) {
    return "未設定";
  }

  if (typeof value === "number") {
    return String(value);
  }

  return value.trim().length > 0 ? value : "-";
}

function renderCount(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "0";
}

function renderDateValue(value: string | null | undefined): string {
  return value && value.trim().length > 0 ? value : "送信履歴なし";
}

function renderBytes(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return "未設定";
  }

  return String(value);
}

function renderList(values: string[] | null | undefined): string {
  return Array.isArray(values) && values.length > 0 ? values.join(", ") : "未設定";
}

function renderConfigStatus(value: string | null | undefined): string {
  if (value === "configured") {
    return "configured";
  }

  if (value === "notConfigured") {
    return "not configured";
  }

  return "未設定";
}

export function AdminIntegrationsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [settings, setSettings] = useState<AdminIntegrationSettingsDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    let cancelled = false;

    async function loadSettings() {
      setLoadState("loading");
      setMessage(null);

      try {
        const nextSettings = await fetchAdminIntegrationSettings(tenantSlug);

        if (cancelled) {
          return;
        }

        setSettings(nextSettings);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setMessage(getProtectedApiErrorMessage(error, "連携設定の取得に失敗しました"));
      }
    }

    void loadSettings();

    return () => {
      cancelled = true;
    };
  }, [hasAdminRole, isSessionReady, navigate, tenantSlug]);

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="連携設定"
      description="LINE / メール / ストレージ / AI / 請求の接続状態を確認します。"
      links={pageLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p style={{ margin: 0 }}>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p style={{ margin: 0 }}>Entry へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p style={{ margin: 0 }}>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p style={{ margin: 0 }}>この画面は admin role のみ利用できます。</p> : null}
        {loadState === "loading" ? <p style={{ margin: 0 }}>連携設定を読み込んでいます。</p> : null}
        {message ? <p style={{ margin: 0 }}>{message}</p> : null}

        {isSessionReady && hasAdminRole && settings ? (
          <div style={{ display: "grid", gap: 12 }}>
            {[
              {
                title: "LINE",
                items: [
                  `LINE login channel: ${renderConfigStatus(settings.line.channelIdStatus)}`,
                  `LIFF: ${settings.line.liffIdSummary ? renderValue(settings.line.liffIdSummary) : "environment-managed"}`,
                  `mini app base: ${settings.line.miniAppBaseUrl ? "configured" : "not configured"}`,
                  `tenant-specific OA: ${settings.line.oaMode}`,
                  `tenant URL: ${settings.line.tenantUrl}`
                ],
                note: settings.line.systemManagedMessage
              },
              {
                title: "メール",
                items: [
                  `owner operational email: ${settings.mail.operationalEmailEnabled ? "enabled" : "disabled"}`,
                  `driver: ${settings.mail.deliveryDriver}`,
                  `fromEmail: ${renderValue(settings.mail.fromEmail)}`,
                  `replyToEmail: ${renderValue(settings.mail.replyToEmail)}`,
                  `queued count: ${renderCount(settings.mail.queuedCount)}`,
                  `processing count: ${renderCount(settings.mail.processingCount)}`,
                  `sent count: ${renderCount(settings.mail.sentCount)}`,
                  `failed count: ${renderCount(settings.mail.failedCount)}`,
                  `retryable count: ${renderCount(settings.mail.retryableCount)}`,
                  `lastSentAt: ${renderDateValue(settings.mail.lastSentAt)}`,
                  `lastFailedAt: ${renderDateValue(settings.mail.lastFailedAt)}`,
                  `lastProcessedAt: ${renderDateValue(settings.mail.lastProcessedAt)}`
                ],
                note: settings.mail.systemManagedMessage
              },
              {
                title: "ストレージ",
                items: [
                  `video upload: ${settings.storage.videoUploadEnabled ? "enabled" : "disabled"}`,
                  `public event attachments: ${settings.storage.publicEventAttachmentsEnabled ? "enabled" : "disabled"}`,
                  `bucket configured: ${settings.storage.bucketConfigured ? "yes" : "no"}`,
                  `bucket driver: ${settings.storage.bucketDriver}`,
                  `max video upload bytes: ${renderBytes(settings.storage.maxVideoUploadSizeBytes)}`,
                  `max public attachment bytes: ${renderBytes(settings.storage.maxPublicEventAttachmentBytes)}`,
                  `supported file types: ${renderList(settings.storage.supportedFileTypes)}`
                ],
                note: settings.storage.systemManagedMessage
              },
              {
                title: "AI",
                items: [
                  `AI features: ${settings.ai.featuresEnabled ? "enabled" : "disabled"}`,
                  `provider: ${settings.ai.provider}`,
                  `aiAssistant entitlement: ${settings.ai.aiAssistantEnabled ? "enabled" : "disabled"}`,
                  `aiFamilyAccountNameSuggestion entitlement: ${settings.ai.aiFamilyAccountNameSuggestionEnabled ? "enabled" : "disabled"}`
                ],
                note: settings.ai.systemManagedMessage
              },
              {
                title: "請求",
                items: [
                  `billingReports entitlement: ${settings.billing.billingReportsEnabled ? "enabled" : "disabled"}`,
                  `billingManagement entitlement: ${settings.billing.billingManagementEnabled ? "enabled" : "disabled"}`,
                  `locked: ${settings.billing.locked ? "yes" : "no"}`
                ],
                note: settings.billing.systemManagedMessage
              }
            ].map((section) => (
              <section
                key={section.title}
                style={{
                  display: "grid",
                  gap: 8,
                  padding: 12,
                  border: "1px solid #e5e7eb",
                  borderRadius: 12
                }}
              >
                <h2 style={{ margin: 0, fontSize: 18 }}>{section.title}</h2>
                <ul style={{ margin: 0, paddingLeft: 20, display: "grid", gap: 6 }}>
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                {section.note ? <div style={{ fontSize: 13, opacity: 0.8 }}>{section.note}</div> : null}
              </section>
            ))}
          </div>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
